import { ThreeDModels } from '@/lib/threejs/config';

// Sensor attachment points for different models
// These define logical positions on models where sensors would be attached

export const getModelSensorPoints = (
  modelType: ThreeDModels,
  basePosition: [number, number, number] = [0, 0, 0],
): Record<string, [number, number, number][]> => {
  const [baseX, baseY, baseZ] = basePosition;

  // Define attachment points for EV charger
  if (modelType === ThreeDModels.EV_CHARGER) {
    return {
      // For EV charger, define logical points for different sensor types
      voltage: [
        [baseX + 1.2, baseY + 1.5, baseZ + 0.3], // Charging port
        [baseX - 0.8, baseY + 0.8, baseZ + 0.8], // Power input
        [baseX + 0.5, baseY + 2.8, baseZ - 0.3], // Display panel
      ],
      power: [
        [baseX + 0.8, baseY + 2.0, baseZ + 0.6], // Power display
        [baseX - 0.6, baseY + 1.8, baseZ - 0.3], // Power controller
        [baseX + 1.0, baseY + 0.9, baseZ + 0.3], // Charging connector
      ],
      temperature: [
        [baseX + 0.0, baseY + 1.0, baseZ - 0.7], // Internal temperature
        [baseX + 0.5, baseY + 0.5, baseZ + 0.6], // External temperature
        [baseX - 0.8, baseY + 2.2, baseZ - 0.4], // Control board temperature
      ],
      current: [
        [baseX + 1.4, baseY + 1.2, baseZ + 0.4], // Main current sensor
        [baseX - 0.7, baseY + 0.7, baseZ + 0.2], // Secondary current sensor
      ],
      energy: [
        [baseX + 0.2, baseY + 2.5, baseZ + 0.2], // Energy meter display
        [baseX - 0.9, baseY + 0.3, baseZ - 0.8], // Energy monitoring unit
      ],
      humidity: [
        [baseX + 0.5, baseY + 1.0, baseZ - 0.5], // Humidity sensor
        [baseX - 0.4, baseY + 2.0, baseZ + 0.3], // Secondary humidity sensor
      ],
      // Default points for any other sensor types
      default: [
        [baseX + 0.7, baseY + 0.8, baseZ + 0.0],
        [baseX - 0.7, baseY + 1.2, baseZ + 0.0],
        [baseX + 0.0, baseY + 2.2, baseZ + 0.7],
        [baseX - 0.5, baseY + 0.8, baseZ - 0.5],
      ],
    };
  }
  // Define attachment points for greenhouse
  else if (modelType === ThreeDModels.GREENHOUSE) {
    return {
      temperature: [
        [baseX - 3.0, baseY + 1.5, baseZ - 2.0], // Inside temperature
        [baseX + 3.0, baseY + 1.5, baseZ + 2.0], // Outside temperature
        [baseX + 0.0, baseY + 2.8, baseZ + 0.0], // Roof temperature
      ],
      humidity: [
        [baseX - 2.0, baseY + 1.0, baseZ + 3.0], // Soil humidity
        [baseX + 2.0, baseY + 2.0, baseZ - 1.5], // Air humidity
        [baseX - 1.5, baseY + 0.5, baseZ - 2.5], // Water system humidity
      ],
      light: [
        [baseX + 0.0, baseY + 2.5, baseZ - 3.0], // Light level top
        [baseX - 2.5, baseY + 1.0, baseZ + 0.0], // Light level side
        [baseX + 2.5, baseY + 1.0, baseZ + 2.0], // Light level corner
      ],
      default: [
        [baseX + 2.0, baseY + 0.5, baseZ - 2.0],
        [baseX - 2.0, baseY + 0.5, baseZ + 2.0],
        [baseX + 0.0, baseY + 1.8, baseZ + 0.0],
        [baseX + 1.5, baseY + 1.2, baseZ + 1.5],
      ],
    };
  }

  // Default empty attachment points
  return {
    default: [[baseX, baseY, baseZ]],
  };
};

// Get a specific attachment point for a sensor based on type
export const getSensorAttachmentPoint = (
  modelType: ThreeDModels,
  sensorType: string,
  basePosition: [number, number, number],
  index = 0,
): [number, number, number] => {
  const attachmentPoints = getModelSensorPoints(modelType, basePosition);

  // Convert sensor type to lowercase for consistent matching
  const normalizedType = sensorType.toLowerCase();

  // Find attachment points for this sensor type, or use default
  const typePoints = attachmentPoints[normalizedType] || attachmentPoints['default'];

  // Use modulo to cycle through available points if index is out of bounds
  const pointIndex = index % typePoints.length;

  return typePoints[pointIndex];
};

/**
 * Creates a grid layout for sensor boxes to ensure consistent positioning
 * This organizes sensors in a structured grid, optionally grouped by sensor type
 *
 * @param sensors Array of sensor objects with id and type
 * @param basePosition Base position of the model [x,y,z]
 * @param gridSize Number of columns in the grid
 * @param spacing Spacing between grid cells
 * @param groupByType Whether to group sensors by their type
 * @returns Record of sensor IDs mapped to their grid positions
 */
export const createSensorGridLayout = (
  sensors: Array<{
    id: string;
    type?: string;
  }>,
  basePosition: [number, number, number] = [0, 0, 0],
  gridSize = 5,
  spacing = 6,
  groupByType = true,
): Record<string, [number, number, number]> => {
  const positions: Record<string, [number, number, number]> = {};
  const [baseX, baseY, baseZ] = basePosition;

  // Place boxes in a more grid-like formation above and around the model
  // This works better with elbow connectors

  if (groupByType) {
    // Group sensors by type
    const sensorsByType: Record<string, string[]> = {};
    sensors.forEach((sensor) => {
      const type = sensor.type?.toLowerCase() || 'default';
      if (!sensorsByType[type]) sensorsByType[type] = [];
      sensorsByType[type].push(sensor.id);
    });

    // Position sensors in a grid, grouped by type
    // Start with a smaller horizontal grid for better organization
    let typeIndex = 0;
    Object.entries(sensorsByType).forEach(([type, sensorIds]) => {
      const typeOffsetX = ((typeIndex % 3) - 1) * (gridSize * spacing * 0.8);
      const typeOffsetZ = Math.floor(typeIndex / 3) * (spacing * 3);

      sensorIds.forEach((sensorId, idx) => {
        const row = Math.floor(idx / 3);
        const col = idx % 3;

        positions[sensorId] = [
          baseX + typeOffsetX + (col - 1) * spacing * 0.6,
          baseY + 4 + row * spacing * 0.4, // Staggered height
          baseZ + typeOffsetZ + 10, // Keep a reasonable distance from model
        ];
      });

      typeIndex++;
    });
  } else {
    // Simple radial-grid layout for more natural organization
    sensors.forEach((sensor, index) => {
      const angle = (index / sensors.length) * Math.PI * 2;
      const radius = 8 + (index % 3) * 2;

      positions[sensor.id] = [
        baseX + Math.sin(angle) * radius,
        baseY + 3 + Math.floor(index / 6) * 2,
        baseZ + Math.cos(angle) * radius,
      ];
    });
  }

  return positions;
};
